/**
 * TableActions - Componente padronizado de ações para tabelas/grids
 *
 * Padroniza os botões de ação (visualizar, editar, excluir, etc.) em todas as grids.
 * Implementado como DropdownMenu shadcn/ui (Radix) com suporte a dark/light mode via CSS vars.
 *
 * @since 2025-12-23
 * @updated 2026-04-06 - Migrado para shadcn DropdownMenu [TH-50457]
 *
 * @example
 * // Uso básico
 * <TableActions
 *   onView={() => navigate(`/resource/${id}`)}
 *   onEdit={() => navigate(`/resource/${id}/edit`)}
 *   onDelete={() => handleDelete(id)}
 * />
 *
 * @example
 * // Com links
 * <TableActions
 *   viewLink={`/resource/${id}`}
 *   editLink={`/resource/${id}/edit`}
 *   onDelete={() => handleDelete(id)}
 * />
 *
 * @example
 * // Ações customizadas
 * <TableActions
 *   actions={[
 *     { key: 'duplicate', icon: 'copy', label: 'Duplicar', onClick: handleDuplicate },
 *     { key: 'export', icon: 'download', label: 'Exportar', onClick: handleExport },
 *   ]}
 *   onDelete={() => handleDelete(id)}
 * />
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  Eye, Pencil, Trash2, Copy, Download, Upload,
  Check, X, XCircle, Plus, Minus, Settings,
  RefreshCw, Link2, Unlink, Archive, Undo2,
  Send, LogIn, MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './Dropdown';

// ============================================
// Types
// ============================================

export type ActionIcon =
  | 'view'
  | 'edit'
  | 'delete'
  | 'copy'
  | 'download'
  | 'upload'
  | 'check'
  | 'x'
  | 'x-circle'
  | 'plus'
  | 'minus'
  | 'settings'
  | 'refresh'
  | 'link'
  | 'unlink'
  | 'archive'
  | 'restore'
  | 'send'
  | 'box-arrow-in-right';

export interface CustomAction {
  key: string;
  icon: ActionIcon;
  label: string;
  onClick: () => void;
  color?: 'blue' | 'amber' | 'green' | 'red' | 'orange' | 'gray' | 'purple';
  disabled?: boolean;
  hidden?: boolean;
}

export interface TableActionsProps {
  /** Link para visualizar (alternativa a onView) */
  viewLink?: string;
  /** Callback para visualizar */
  onView?: () => void;
  /** Tooltip do botão visualizar */
  viewLabel?: string;
  /** Esconder botão visualizar */
  hideView?: boolean;

  /** Link para editar (alternativa a onEdit) */
  editLink?: string;
  /** Callback para editar */
  onEdit?: () => void;
  /** Tooltip do botão editar */
  editLabel?: string;
  /** Esconder botão editar */
  hideEdit?: boolean;

  /** Callback para excluir */
  onDelete?: () => void;
  /** Tooltip do botão excluir */
  deleteLabel?: string;
  /** Esconder botão excluir */
  hideDelete?: boolean;

  /** Ações customizadas adicionais */
  actions?: CustomAction[];

  /** Tamanho dos ícones */
  size?: 'sm' | 'md' | 'lg';

  /** Desabilitar todas as ações */
  disabled?: boolean;
}

// ============================================
// Icon mapping (lucide-react) [TH-49945]
// ============================================

const lucideIcons: Record<ActionIcon, React.FC<{ className?: string }>> = {
  view: Eye,
  edit: Pencil,
  delete: Trash2,
  copy: Copy,
  download: Download,
  upload: Upload,
  check: Check,
  x: X,
  'x-circle': XCircle,
  plus: Plus,
  minus: Minus,
  settings: Settings,
  refresh: RefreshCw,
  link: Link2,
  unlink: Unlink,
  archive: Archive,
  restore: Undo2,
  send: Send,
  'box-arrow-in-right': LogIn,
};

// ============================================
// Icon Component
// ============================================

const ActionIconSvg: React.FC<{ icon: ActionIcon; className?: string }> = ({ icon, className }) => {
  const LucideIcon = lucideIcons[icon];
  return <LucideIcon className={cn('h-4 w-4 flex-shrink-0', className)} />;
};

// Color classes para ícones nos itens do menu
const iconColorClasses: Record<NonNullable<CustomAction['color']>, string> = {
  blue: 'text-foreground/80',
  amber: 'text-amber-600 dark:text-amber-400',
  green: 'text-green-600 dark:text-green-400',
  red: 'text-red-600 dark:text-red-400',
  orange: 'text-orange-600 dark:text-orange-400',
  gray: 'text-muted-foreground',
  purple: 'text-purple-600 dark:text-purple-400',
};

// ============================================
// Trigger Button (três pontos verticais)
// ============================================

const TriggerButton = React.forwardRef<
  HTMLButtonElement,
  { size: 'sm' | 'md' | 'lg'; disabled?: boolean }
>(({ size, disabled, ...props }, ref) => {
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-7 w-7',
    lg: 'h-8 w-8',
  };

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center rounded-lg',
        'text-muted-foreground hover:text-foreground',
        'hover:bg-accent transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        sizeClasses[size]
      )}
      aria-label="Mais ações"
      {...props}
    >
      <MoreVertical className={size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />
    </button>
  );
});
TriggerButton.displayName = 'TriggerButton';

// ============================================
// Main Component
// ============================================

export const TableActions: React.FC<TableActionsProps> = ({
  viewLink,
  onView,
  viewLabel,
  hideView = false,

  editLink,
  onEdit,
  editLabel,
  hideEdit = false,

  onDelete,
  deleteLabel,
  hideDelete = false,

  actions = [],

  size = 'md',
  disabled = false,
}) => {
  const { t } = useTranslation('buttons');

  const finalViewLabel = viewLabel ?? t('view_details');
  const finalEditLabel = editLabel ?? t('edit');
  const finalDeleteLabel = deleteLabel ?? t('delete');

  const showView = !hideView && (viewLink || onView);
  const showEdit = !hideEdit && (editLink || onEdit);
  const showDelete = !hideDelete && onDelete;

  const visibleActions = actions.filter((action) => !action.hidden);

  const hasCustomActions = visibleActions.length > 0;
  const hasAnyAction = showView || showEdit || showDelete || hasCustomActions;

  if (!hasAnyAction) return null;

  // Verifica se há ações de delete para adicionar separador
  const hasNonDeleteActions = showView || showEdit || hasCustomActions;

  return (
    <div className="flex items-center justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <TriggerButton size={size} disabled={disabled} />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" sideOffset={4}>
          {/* Ações customizadas (antes das padrão) */}
          {visibleActions.map((action) => (
            <DropdownMenuItem
              key={action.key}
              disabled={disabled || action.disabled}
              onSelect={() => {
                if (!disabled && !action.disabled) {
                  action.onClick();
                }
              }}
              className={cn(
                'flex items-center gap-2 text-sm cursor-default',
                (disabled || action.disabled) && 'pointer-events-none opacity-50'
              )}
            >
              <ActionIconSvg
                icon={action.icon}
                className={iconColorClasses[action.color ?? 'gray']}
              />
              <span>{action.label}</span>
            </DropdownMenuItem>
          ))}

          {/* Visualizar */}
          {showView && (
            viewLink ? (
              <DropdownMenuItem asChild>
                <Link
                  to={viewLink}
                  className="flex items-center gap-2 text-sm cursor-default"
                >
                  <ActionIconSvg icon="view" className={iconColorClasses.blue} />
                  <span>{finalViewLabel}</span>
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={disabled}
                onSelect={() => {
                  if (!disabled && onView) onView();
                }}
                className="flex items-center gap-2 text-sm cursor-default"
              >
                <ActionIconSvg icon="view" className={iconColorClasses.blue} />
                <span>{finalViewLabel}</span>
              </DropdownMenuItem>
            )
          )}

          {/* Editar */}
          {showEdit && (
            editLink ? (
              <DropdownMenuItem asChild>
                <Link
                  to={editLink}
                  className="flex items-center gap-2 text-sm cursor-default"
                >
                  <ActionIconSvg icon="edit" className={iconColorClasses.amber} />
                  <span>{finalEditLabel}</span>
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={disabled}
                onSelect={() => {
                  if (!disabled && onEdit) onEdit();
                }}
                className="flex items-center gap-2 text-sm cursor-default"
              >
                <ActionIconSvg icon="edit" className={iconColorClasses.amber} />
                <span>{finalEditLabel}</span>
              </DropdownMenuItem>
            )
          )}

          {/* Separador antes de excluir */}
          {showDelete && hasNonDeleteActions && <DropdownMenuSeparator />}

          {/* Excluir */}
          {showDelete && (
            <DropdownMenuItem
              disabled={disabled}
              onSelect={() => {
                if (!disabled && onDelete) onDelete();
              }}
              className={cn(
                'flex items-center gap-2 text-sm cursor-default',
                'text-red-600 dark:text-red-400',
                'focus:bg-red-50 dark:focus:bg-red-500/10',
                'focus:text-red-600 dark:focus:text-red-400'
              )}
            >
              <ActionIconSvg icon="delete" className="text-red-600 dark:text-red-400" />
              <span>{finalDeleteLabel}</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default TableActions;
